import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Stack,
    TextField,
    useTheme,
    IconButton,
    Tooltip,
    Grid
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';

const PdfSeparator = () => {
    const theme = useTheme();
    const [file, setFile] = useState(null);
    const [range, setRange] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dragActive, setDragActive] = useState(false);

    const processFile = (selectedFile) => {
        setError('');
        setSuccess('');

        if (selectedFile.type !== 'application/pdf') {
            setError('Please upload a valid PDF file.');
            return;
        }
        if (selectedFile.size > 10 * 1024 * 1024) {
            setError('File size exceeds 10MB limit.');
            return;
        }

        setFile(selectedFile);
        const url = URL.createObjectURL(selectedFile);
        setPreviewUrl(url);
    };

    const handleFileChange = (event) => {
        if (event.target.files[0]) {
            processFile(event.target.files[0]);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleSplit = async () => {
        if (!file) return;

        setLoading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('file', file);
        if (range) {
            formData.append('pages', range);
        }

        try {
            const response = await fetch('/api/split-pdf', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to separate PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `separated_${file.name.replace('.pdf', '')}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            setSuccess('PDF separated successfully! ZIP downloaded.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, color: theme.palette.text.primary }}>
                PDF Separator
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Split PDF into custom page ranges or individual pages. No files are stored on our servers.
            </Typography>

            <Grid container spacing={3}>
                {/* Top Section: Upload & Options */}
                <Grid item xs={12} md={file ? 6 : 12}>
                    <Stack spacing={3}>
                        {/* File Upload / Selected File */}
                        <Tooltip title="Drag & drop your PDF file here, or click to browse" arrow>
                            <Paper
                                elevation={0}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                sx={{
                                    p: 4,
                                    border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.divider}`,
                                    borderRadius: 4,
                                    textAlign: 'center',
                                    bgcolor: dragActive ? (theme.palette.mode === 'dark' ? 'rgba(0, 242, 255, 0.05)' : 'rgba(0, 120, 255, 0.05)') : theme.palette.background.default,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    minHeight: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onClick={() => document.getElementById('pdf-upload').click()}
                            >
                                <input
                                    accept="application/pdf"
                                    style={{ display: 'none' }}
                                    id="pdf-upload"
                                    type="file"
                                    onChange={handleFileChange}
                                />

                                {file ? (
                                    <Stack alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                        <Stack direction="row" alignItems="center" spacing={2} sx={{
                                            p: 2,
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                            borderRadius: 2,
                                            width: '100%',
                                            maxWidth: 400
                                        }}>
                                            <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                                            <Box sx={{ flexGrow: 1, textAlign: 'left', overflow: 'hidden' }}>
                                                <Tooltip title={file.name} placement="top">
                                                    <Typography variant="subtitle1" noWrap>{file.name}</Typography>
                                                </Tooltip>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                                </Typography>
                                            </Box>
                                            <Tooltip title="Remove file" arrow>
                                                <IconButton size="small" onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFile(null);
                                                    setPreviewUrl(null);
                                                }}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack alignItems="center" spacing={2}>
                                        <CloudUploadIcon sx={{ fontSize: 64, color: dragActive ? 'primary.main' : 'text.secondary', opacity: dragActive ? 1 : 0.5 }} />
                                        <Typography variant="h6">
                                            {dragActive ? "Drop PDF here" : "Click or Drag PDF here"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Limit 10MB
                                        </Typography>
                                    </Stack>
                                )}
                            </Paper>
                        </Tooltip>

                        {/* Alerts */}
                        {error && <Alert severity="error">{error}</Alert>}
                        {success && <Alert severity="success">{success}</Alert>}
                    </Stack>
                </Grid>

                {/* Separation Options - Top Right (Visible only when file is uploaded) */}
                {file && (
                    <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 3, bgcolor: theme.palette.background.paper, borderRadius: 2, height: '100%' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Separation Options
                            </Typography>

                            <Tooltip title="Specify pages to extract (e.g., separate by comma or use hyphens for ranges)" arrow placement="top">
                                <TextField
                                    fullWidth
                                    label="Page Ranges (Optional)"
                                    placeholder="e.g., 3-5,7,8-9"
                                    value={range}
                                    onChange={(e) => setRange(e.target.value)}
                                    helperText={
                                        <Box component="span">
                                            <strong>Format:</strong>
                                            <br />
                                            • Single pages: <code>7</code> → 1 document with page 7
                                            <br />
                                            • Ranges: <code>3-5</code> → 1 document with pages 3-5
                                            <br />
                                            • Multiple groups: <code>3-5,7,8-9</code> → 3 documents
                                            <br />
                                            • Leave empty to split all pages individually
                                        </Box>
                                    }
                                    sx={{ mb: 2 }}
                                />
                            </Tooltip>

                            <Tooltip title="Process and download the separated PDF files as a ZIP archive">
                                <span> {/* wrapper for disabled state tooltip */}
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CallSplitIcon />}
                                        onClick={handleSplit}
                                        disabled={loading}
                                        sx={{ mt: 2 }}
                                    >
                                        {loading ? 'Processing...' : 'Separate & Download ZIP'}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Bottom Section: PDF Preview (Full Width, outside Grid) */}
            {file && previewUrl && (
                <Box sx={{ mt: 4, width: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        PDF Preview
                    </Typography>
                    <Tooltip title="Preview of the uploaded PDF" arrow placement="top">
                        <Box sx={{
                            height: '100vh', // Use viewport height for massive preview
                            maxHeight: 1200,
                            bgcolor: 'grey.100',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`,
                            width: '100%'
                        }}>
                            <iframe
                                src={previewUrl}
                                title="PDF Preview"
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        </Box>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default PdfSeparator;
